// UserContext.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "./supabaseClient";
import { AuthModal } from "./AuthModal";
import {
  clearKeyAndSaltFromSessionStorage,
  getUserSettingsFromDbWithKey,
  loadKeyAndSaltFromSessionStorage,
  saveKeyAndSaltToSessionStorage,
  sendUserSettingsToDb,
} from "./crypto-utils";

type UserContextType = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  logout: () => Promise<void>;
  showAuthUI: () => void;
  userSettings: object | null;
  clearUserSettings: () => void;
  saveUserSettings: ((settings: object | null) => Promise<void>) | null;
  isFetchingSettings: boolean;
};

const UserContext = createContext<UserContextType>({
  user: null,
  session: null,
  loading: true,
  logout: async () => {},
  showAuthUI: () => {},
  userSettings: null,
  clearUserSettings: () => {},
  saveUserSettings: null,
  isFetchingSettings: false,
});

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [authOpen, setAuthOpen] = useState(false);
  const [userSettings, setUserSettings] = useState<object | null>(null);
  const [isFetchingSettings, setIsFetchingSettings] = useState(false);

  useEffect(() => {
    const init = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // If the user has just logged in to the same session, we want to try to load their settings
      const userLoggedIn = !user && session?.user != null;
      if (userLoggedIn) {
        console.log("User logged in, attempting to load session settings...");
        setIsFetchingSettings(true);
        loadKeyAndSaltFromSessionStorage()
          .then((derived) => {
            if (derived != null) {
              const { key } = derived;
              return getUserSettingsFromDbWithKey(session?.user, key);
            }
            // Soft fail if derived is null (e.g., if the user has logged in with a different session)
          })
          .then((settings) => {
            if (settings) {
              console.log("Fetched settings for session.");
              setUserSettings(settings);
              setIsFetchingSettings(false);
            } else {
              console.log("No settings found for user.");
            }
          })
          .catch((error) => {
            console.error("Failed to reload user settings for session:", error);
            setIsFetchingSettings(false);
          });
      }
    };

    init();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) setAuthOpen(false); // Close auth modal on login
    });

    return () => subscription.unsubscribe();
  }, []);

  const logout = async () => {
    // Remove this session's derived key and salt from local storage
    clearKeyAndSaltFromSessionStorage();

    // Log out the user
    await supabase.auth.signOut();
  };

  const showAuthUI = () => {
    setAuthOpen(true);
  };

  const saveUserSettings = async (
    settings: object | null,
    sessionUser?: User | null,
  ) => {
    const _user = sessionUser || user;
    if (settings == null) {
      if (_user) {
        // If settings are null, delete the settings from the database
        const { error } = await supabase
          .from("settings")
          .delete()
          .eq("user_id", _user.id);
        // Soft fail
        if (!error) {
          console.log("User settings deleted successfully from server.");
        } else {
          console.error(error);
        }
      }
      return;
    }

    // If settings are not null, save them to the database
    const derived = await loadKeyAndSaltFromSessionStorage();
    if (!derived) {
      window.alert(
        "Unable to save settings to account: could not find encryption token. This can happen if you have an expired session. Try logging out and back in.",
      );
      return;
    }
    try {
      const { key, salt } = derived;
      await sendUserSettingsToDb(settings, _user, key, salt);
    } catch (error) {
      console.error("Failed to save encrypted user settings to server:", error);
      window.alert("Failed to save settings. Please try again.");
    }
  };

  return (
    <UserContext.Provider
      value={{
        user,
        session,
        loading,
        logout,
        showAuthUI,
        userSettings,
        clearUserSettings: () => setUserSettings(null),
        saveUserSettings,
        isFetchingSettings,
      }}
    >
      {children}
      {
        authOpen && (
          <AuthModal
            opened={authOpen}
            onClose={() => setAuthOpen(false)}
            saveUserSettings={saveUserSettings}
            onLoadSettings={setUserSettings}
            setIsFetchingSettings={setIsFetchingSettings}
            onLoadDerivedKey={({ key, salt }) =>
              saveKeyAndSaltToSessionStorage(key, salt)
            }
          />
        )
        // ReactDOM.createPortal(
        //   <div
        //     style={{
        //       position: "fixed",
        //       top: 0,
        //       left: 0,
        //       width: "100vw",
        //       height: "100vh",
        //       background: "rgba(0, 0, 0, 0.7)",
        //       display: "flex",
        //       justifyContent: "center",
        //       alignItems: "center",
        //       zIndex: 1000,
        //     }}
        //     onClick={() => setAuthOpen(false)}
        //   >
        //     <div
        //       style={{
        //         background: "#fff",
        //         padding: 24,
        //         borderRadius: 8,
        //         minWidth: 320,
        //         width: 320,
        //       }}
        //       onClick={(e) => e.stopPropagation()}
        //     >
        //       <Flex align="center" justify="left">
        //         <Title mr="sm" order={3}>
        //           Login to <span className="cf-header">ChainForge.ai</span>
        //         </Title>
        //         <Tooltip
        //           label="Log in to save your flows and securely store your API keys, so you don't have to set them every visit."
        //           multiline
        //           w="180px"
        //           withArrow
        //           position="right-start"
        //         >
        //           <IconInfoCircle size={22} color="gray" />
        //         </Tooltip>
        //       </Flex>

        //       <Auth
        //         supabaseClient={supabase}
        //         appearance={{ theme: ThemeSupa }}
        //         providers={[]}
        //       />
        //     </div>
        //   </div>,
        //   document.body,
        // )
      }
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
